import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SITES PAGE - PRODUCTION VERSION
 *
 * This file uses real API calls to fetch and manage sites.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SitesTable } from "./components";
import { Pagination } from "@/components/common/Pagination";
import { PageHeader } from "@/components/common/PageHeader";
import { PageHeaderWithBack } from "@/components/common";
import { Button } from "@/components/common/Button";
import { useToast } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useAlert } from "@/hooks/useAlert";
import { sitesService } from "@/api/services/sites.service";
import styles from "./Sites.module.css";
// Import duplicates styles from estimating (reuse)
import duplicatesStyles from "../estimating/Estimating.module.css";
// Building type options for filter
const BUILDING_TYPE_OPTIONS = [
    { value: "", label: "All Building Types" },
    { value: "COMMERCIAL", label: "Commercial" },
    { value: "RETAIL", label: "Retail" },
    { value: "HOSPITALITY", label: "Hospitality" },
    { value: "INDUSTRIAL", label: "Industrial" },
];
export const Sites = () => {
    const navigate = useNavigate();
    const { user, isAdmin } = useAuth();
    const { error: showError } = useToast();
    const { confirm } = useAlert();
    const [searchParams, setSearchParams] = useSearchParams();
    const isMountedRef = useRef(true);
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    // Filter state
    const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
    const [buildingTypeFilter, setBuildingTypeFilter] = useState(searchParams.get("buildingType") || "");
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    // Duplicates state
    const [duplicateCount, setDuplicateCount] = useState(0);
    // Delete state
    const [isDeleting, setIsDeleting] = useState(null);
    const fetchSites = useCallback(async () => {
        try {
            if (!isMountedRef.current)
                return;
            setLoading(true);
            setError(null);
            const filters = {
                search: searchTerm || undefined,
                buildingType: buildingTypeFilter || undefined,
                page: currentPage,
            };
            const response = await sitesService.getSites(filters);
            // Check if component is still mounted before updating state
            if (!isMountedRef.current)
                return;
            const { data, pagination } = response.data;
            setSites(data);
            setCurrentPage(pagination.pageNumber);
            setTotalPages(pagination.totalPages);
            setPageSize(pagination.pageSize);
        }
        catch (err) {
            // Ignore errors if component unmounted or request was cancelled
            if (!isMountedRef.current)
                return;
            // Ignore cancellation errors (they're expected when component unmounts)
            if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
                return;
            }
            setError("Failed to load sites");
            console.error("Error fetching sites:", err);
        }
        finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [searchTerm, buildingTypeFilter, currentPage]);
    useEffect(() => {
        isMountedRef.current = true;
        fetchSites();
        return () => {
            isMountedRef.current = false;
        };
    }, [fetchSites]);
    // Fetch duplicate count on mount
    const fetchDuplicates = useCallback(async () => {
        try {
            const response = await sitesService.getDuplicateSites();
            setDuplicateCount(response.data.totalDuplicateGroups);
        }
        catch (err) {
            console.error("Failed to fetch duplicates:", err);
        }
    }, []);
    useEffect(() => {
        fetchDuplicates();
    }, [fetchDuplicates]);
    const handleManageDuplicates = () => {
        navigate(`${baseRoute}/sites/duplicates`);
    };
    const handleSearch = (e) => {
        if (e.key === "Enter") {
            const params = {};
            if (searchTerm)
                params.search = searchTerm;
            if (buildingTypeFilter)
                params.buildingType = buildingTypeFilter;
            setSearchParams(params);
            setCurrentPage(1);
        }
    };
    const handleBuildingTypeFilter = (buildingType) => {
        setBuildingTypeFilter(buildingType);
        setCurrentPage(1);
        setShowFilterMenu(false);
        const params = {};
        if (searchTerm)
            params.search = searchTerm;
        if (buildingType)
            params.buildingType = buildingType;
        setSearchParams(params);
    };
    const handleClearFilters = () => {
        setBuildingTypeFilter("");
        setSearchParams({});
        setCurrentPage(1);
        setShowFilterMenu(false);
    };
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const baseRoute = isAdmin ? "/admin" : "/staff";
    const handleEditClick = (siteId) => {
        navigate(`${baseRoute}/sites/${siteId}/edit`);
    };
    const handleDeleteClick = async (siteId) => {
        const confirmed = await confirm("Are you sure you want to delete this site?");
        if (!confirmed)
            return;
        try {
            setIsDeleting(siteId);
            await sitesService.deleteSite(siteId);
            await fetchSites();
        }
        catch (err) {
            console.error("Error deleting site:", err);
            showError("Failed to delete site");
        }
        finally {
            setIsDeleting(null);
        }
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Sites", onBack: () => navigate(-1), actions: _jsxs("div", { className: styles.actions, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsx("span", { className: styles.searchIcon, children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }) }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), onKeyPress: handleSearch, autoFocus: true })] }), _jsxs("div", { className: styles.filterDropdown, children: [_jsxs("button", { className: styles.filterBtn, onClick: () => setShowFilterMenu(!showFilterMenu), title: "Filter sites", children: [_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "currentColor", children: _jsx("path", { d: "M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" }) }), buildingTypeFilter && (_jsx("span", { className: styles.filterBadge, children: "1" }))] }), showFilterMenu && (_jsxs("div", { className: styles.filterMenu, children: [buildingTypeFilter && (_jsx("button", { onClick: handleClearFilters, className: styles.filterOption, children: "Clear Filters" })), BUILDING_TYPE_OPTIONS.filter((opt) => opt.value !== "").map((option) => (_jsx("button", { onClick: () => handleBuildingTypeFilter(option.value), className: `${styles.filterOption} ${buildingTypeFilter === option.value
                                                ? styles.activeFilter
                                                : ""}`, children: option.label }, option.value)))] }))] }), duplicateCount > 0 && (_jsxs("button", { className: duplicatesStyles.duplicatesBtn, onClick: handleManageDuplicates, children: [_jsx("span", { className: duplicatesStyles.duplicatesBadge, children: duplicateCount }), "Manage Duplicates"] })), _jsx(Button, { className: styles.addSiteButton, onClick: () => navigate(`${baseRoute}/sites/create`), variant: "secondary", children: "Add Site" })] }) }), loading && (_jsx("div", { className: styles.loading, children: _jsx("p", { children: "Loading sites..." }) })), error && (_jsxs("div", { className: styles.error, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchSites, className: styles.retryBtn, children: "Retry" })] })), !loading && !error && (_jsxs("div", { className: "table-page-listing", children: [_jsx(SitesTable, { sites: sites, onEditClick: handleEditClick, isAdmin: isAdmin, onDeleteClick: handleDeleteClick, isDeleting: isDeleting }), _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, pageSize: pageSize, onPageChange: handlePageChange })] }))] }));
};
